import { getOptionsTokensFromRules } from "./whatsappFlowValidationCodec";

export const OPTION_HANDLE_PREFIX = "option-";

export const optionSourceHandleId = (index) => `${OPTION_HANDLE_PREFIX}${index}`;

export const branchNodeId = (parentId, index) => `branch-${parentId}-opt-${index}`;

/**
 * Alinea nodos y edges de salida con la regla OPTIONS (handles `option-0` …).
 * @param {import("@xyflow/react").Node} parentNode
 * @param {string[]} optionLabels
 * @param {import("@xyflow/react").Node[]} nodes
 * @param {import("@xyflow/react").Edge[]} edges
 * @param {(id: string, stepNumber: number, position: { x: number; y: number }) => import("@xyflow/react").Node} createWhatsappFlowNode
 * @returns {{ nodes: import("@xyflow/react").Node[], edges: import("@xyflow/react").Edge[] }}
 */
export function syncOptionBranches(
  parentNode,
  optionLabels,
  nodes,
  edges,
  createWhatsappFlowNode
) {
  const parentId = parentNode.id;
  const pos = parentNode.position || { x: 0, y: 0 };
  const n = optionLabels.length;

  let nextNodes = nodes.map((node) => ({
    ...node,
    data: node.data ? { ...node.data } : {},
  }));
  let nextEdges = edges.map((edge) => ({ ...edge }));

  nextEdges = nextEdges.filter(
    (e) =>
      !(
        e.source === parentId &&
        e.sourceHandle &&
        String(e.sourceHandle).startsWith(OPTION_HANDLE_PREFIX)
      )
  );

  const branchPrefix = `branch-${parentId}-opt-`;
  const toRemove = new Set();
  nextNodes.forEach((node) => {
    const b = node.data?.autoOptionBranch;
    if (b && b.parentId === parentId && Number(b.index) >= n) {
      toRemove.add(node.id);
    }
    if (typeof node.id === "string" && node.id.startsWith(branchPrefix)) {
      const idx = parseInt(node.id.slice(branchPrefix.length), 10);
      if (!Number.isNaN(idx) && idx >= n) {
        toRemove.add(node.id);
      }
    }
  });
  nextNodes = nextNodes.filter((node) => !toRemove.has(node.id));
  nextEdges = nextEdges.filter(
    (e) => !toRemove.has(e.source) && !toRemove.has(e.target)
  );

  if (n === 0) {
    return { nodes: nextNodes, edges: nextEdges };
  }

  let legacyTarget = null;
  const legI = nextEdges.findIndex(
    (e) => e.source === parentId && (e.sourceHandle == null || e.sourceHandle === "")
  );
  if (legI >= 0) {
    legacyTarget = nextEdges[legI].target;
    nextEdges.splice(legI, 1);
  }

  const maxStep = nextNodes.reduce((acc, node) => {
    const m = /^Paso (\d+)$/.exec(node.data?.name || "");
    return m ? Math.max(acc, parseInt(m[1], 10)) : acc;
  }, 0);
  let stepCounter = maxStep;

  for (let i = 0; i < n; i++) {
    const h = optionSourceHandleId(i);
    const bid = branchNodeId(parentId, i);
    const label = optionLabels[i];

    const existingEdge = edges.find((e) => e.source === parentId && e.sourceHandle === h);
    let targetId = existingEdge?.target;

    if (!targetId && i === 0 && legacyTarget && nextNodes.some((x) => x.id === legacyTarget)) {
      targetId = legacyTarget;
    }

    if (!targetId || !nextNodes.some((x) => x.id === targetId)) {
      stepCounter += 1;
      const newNode = createWhatsappFlowNode(bid, stepCounter, {
        x: pos.x + 340,
        y: pos.y + i * 130,
      });
      newNode.data = {
        ...newNode.data,
        name: `Rama: ${label}`,
        autoOptionBranch: { parentId, index: i, label },
      };
      const existingIdx = nextNodes.findIndex((x) => x.id === bid);
      if (existingIdx >= 0) {
        nextNodes[existingIdx] = newNode;
      } else {
        nextNodes.push(newNode);
      }
      targetId = bid;
    } else {
      nextNodes = nextNodes.map((node) =>
        node.id === targetId
          ? {
              ...node,
              data: {
                ...node.data,
                name: `Rama: ${label}`,
                autoOptionBranch: { parentId, index: i, label },
              },
            }
          : node
      );
    }

    nextEdges.push({
      id: `e-${parentId}-${h}-${targetId}`,
      type: "smoothstep",
      source: parentId,
      target: targetId,
      sourceHandle: h,
    });
  }

  return { nodes: nextNodes, edges: nextEdges };
}

export const getOptionLabelsForNode = (parentNode) =>
  getOptionsTokensFromRules(parentNode?.data?.validationRules || []);
